import React, {useContext, useEffect, useState} from 'react';
import {Container, Row, Col} from 'reactstrap';
import {gql, useSubscription, useMutation} from '@apollo/client';
import useLocalStorage from 'react-use-localstorage';
import {useHistory} from "react-router-dom";

import {InLinePreLoader} from '../components/preloader';
import SidebarQuizUser from '../components/sidebar-quiz-user';
import QuizUserForm from '../components/quiz-user-form';
import {Question} from '../types/quiz'
import {Presentation} from '../types/presentation';
import SideBarBtns from '../components/sidebar-btns';
import {useTimer} from 'use-timer';
import {ReturnValue} from 'use-timer/lib/types';
import EventContext from '../contexts/event';
import UserContext from '../contexts/user';
import LocaleSwitcher from '../components/locale-switcher';
import TopBar from '../components/top-bar';
import QuizResultUserForm from '../components/quiz-result-user-form';
import MobileBottomNav from "../components/mobile-bottom-nav";
import {useIntl} from "react-intl";
import {quiz_actions} from '../gql/quiz-gql';
import { isQuizScheduleWindowActive } from '../helpers/quiz-schedule-time';

const presentation_subscription = gql`
  subscription quiz_user_presentation($id: String!) {
    presentation_by_pk(id: $id) {
      id
      live
      questions_from_time
      questions_to_time
      results_from_time
      results_to_time
    }
  }
`

const questions_subscription = gql`
  subscription quiz_user_questions($presentation_id: String!) {
    question(
      where: {
        presentation_id: { _eq: $presentation_id }
        visible: { _eq: true }
      }
      order_by: { order: asc }
    ) {
      id
      image
      order
      question
      visible
      is_survey
      options {
        id
        option
        order
        correct
      }
      responses {
        options {
          question_option_id
        }
        user_id
        question_id
      }
    }
  }
`

const LIVE_QUIZ_STARTED_ACTIONS = new Set([
  'start_quiz',
  'timer_start',
  'next_question',
  'timer_stop',
  'show_result',
  'timer_end',
  'final',
]);

function isLiveQuizStarted(actions: Array<{ action?: string | null }> = []): boolean {
  return actions.some(
    (row) => row.action != null && LIVE_QUIZ_STARTED_ACTIONS.has(row.action)
  );
}

const answer_for_question = gql`
  mutation answer_for_question (
    $question_id: uuid!
    $option_ids: [uuid!]!
    $time: numeric!
  ) {
    answer_for_question(
      question_id: $question_id
      option_ids: $option_ids
      time: $time
    ) {
      id
    }
  }
`
const QuizUser: React.FC<{
    ppt: Presentation;
    locale: string;
    forum?: boolean;
    setLocale: (arg0: string) => void;
}> = ({ppt, locale, setLocale, forum}) => {
    const history = useHistory();

    const eventContext = useContext(EventContext);
    const userContext = useContext(UserContext);
    const [showForumSidebar, setShowForumSidebar] = useState(forum);

    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    // @ts-ignore
    const [previousTime, setPreviousTime] = useLocalStorage(`quiz-timer-${userContext.user?.id}-${currentQuestion?.id}`, "0");


    const timer = useTimer({
        interval: 100,
        autostart: false,
        step: 0.1,
        initialTime: 0,
        onTimeUpdate: (t) => {
            if (currentQuestion) {
                setPreviousTime(JSON.stringify(t));
            }
        },
    });

    return (
        <>
            <SideBarBtns presentation={ppt}/>
            <div className="grid-contianer quiz-user-container">
                <LocaleSwitcher locale={locale} setLocale={setLocale}/>
                <TopBar
                    presentation={ppt}
                    setShowForumSidebar={setShowForumSidebar}
                    forumIsOpen={false}
                    forum={false}
                    tab={1}
                    hasVideo={ppt.video ? true : false}
                />
                <div className="sidebar">
                    <SidebarQuizUser presentation={ppt} timer={timer} is_survey={currentQuestion?.is_survey}/>
                </div>
                <div className="main c-pad-fix">
                    <Container>
                        <MobileBottomNav presentation={ppt} tab={2}/>
                        <Row >
                            <QuizQuestions
                                ppt={ppt}
                                timer={timer}
                                setCurrentQuestion={setCurrentQuestion}
                            />
                        </Row>
                    </Container>
                </div>
            </div>
        </>
    )
}

const QuizQuestions: React.FC<{
    ppt: Presentation
    timer: ReturnValue;
    setCurrentQuestion: (q: Question | null) => void;
}> = ({ppt, timer, setCurrentQuestion}) => {
    const userContext = useContext(UserContext);
    const intl = useIntl();
    const [scheduleTick, setScheduleTick] = useState(0);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setScheduleTick((tick) => tick + 1);
        }, 30000);

        return () => window.clearInterval(intervalId);
    }, []);

    const {
        data: presentationData,
        loading: presentationQueryLoading,
        error: presentationError
    } = useSubscription<{ presentation_by_pk: {
        id: string;
        live: boolean;
        questions_from_time?: string | null;
        questions_to_time?: string | null;
        results_from_time?: string | null;
        results_to_time?: string | null;
    } | null }>(presentation_subscription, {
        variables: { id: ppt._id },
        skip: !ppt?._id,
    });

    const {
        data: questionsData,
        loading: questionsLoading,
        error: questionsError,
    } = useSubscription<{ question: Question[] }>(questions_subscription, {
        variables: { presentation_id: ppt._id },
        skip: !ppt?._id,
    });

    const { data: quizActionsData } = useSubscription(quiz_actions, {
        variables: { presentation_id: ppt._id },
        skip: !ppt?._id,
    });

    const [submit_answer] = useMutation(answer_for_question, {
        update: (_, {data: {answer_for_question}}) => {
             console.log('debug-xxx : question_response: ', answer_for_question);
            setTimeout(() => {
                localStorage.removeItem(`quiz-timer-${userContext.user?.id}-${answer_for_question.id}`)
            }, 2000);
        },
        onError(error) {
             console.log('debug-xxx : question_response: ', error);
        },
    });

    if ((presentationQueryLoading && !presentationData) || (questionsLoading && !questionsData)) {
        return <InLinePreLoader/>
    }

    if (presentationError || questionsError) {
         console.log('debug: presentationError: ', presentationError ?? questionsError)
        return <i>There was an error</i>
    }

    void scheduleTick;

    const presentation = presentationData?.presentation_by_pk;

    if (!presentation) {
        return <i>The quiz for this presentation is not available yet</i>
    }

    const questions = questionsData?.question ?? [];
    const quizActions = quizActionsData?.quiz_actions ?? [];

    let show_quiz: boolean;
    let show_results: boolean;

    if (presentation.live) {
        show_quiz = isLiveQuizStarted(quizActions);
        show_results = quizActions.some((row: { action?: string | null }) => row.action === 'show_result');
    } else {
        show_quiz = isQuizScheduleWindowActive(
            presentation.questions_from_time,
            presentation.questions_to_time
        );
        show_results = isQuizScheduleWindowActive(
            presentation.results_from_time,
            presentation.results_to_time
        );
    }

    if (!show_quiz && !show_results) {
        return <i>The quiz for this presentation is not available yet</i>
    }

    if (questions.length === 0) {
        return <i>{intl.formatMessage({id:'quiz.no.questions'})}</i>
    }

    var remaining_questions = questions
        .sort((a, b) => a.order - b.order);

    var quiz_questions: Question[] = [];
    var questionAlreadyAttented = false;
    for (var i = 0; i < questions.length; i++) {
        if (questions[i].responses == undefined || questions[i].responses.length == 0) {
            quiz_questions.push(questions[i]);
        } else {
            var alredayAttend = false;
            for (var k = 0; k < questions[i].responses.length; k++) {
                if (questions[i].responses[k].user_id === userContext.user?.id) {
                    alredayAttend = true;
                    questionAlreadyAttented = true;
                    break;
                }else{
                    questionAlreadyAttented = false;
                }
            }
            if (!alredayAttend) {
                quiz_questions.push(questions[i]);
            }
        }
    }

    remaining_questions = quiz_questions;

    if (remaining_questions.length === 0) {
        if (show_results) {
            return <Col>{
                questions
                    .sort((a, b) => a.order - b.order)
                    .map((question, index) => (
                        <QuizResultUserForm
                            key={question.id}
                            question={question}
                            index={index}
                            totalQuestions={questions.length}
                            show_quiz={show_quiz}
                            show_results={show_results}
                            submit_answer={submit_answer}
                            timer={timer}
                        />
                    ))
            }
            </Col>
        } else {
            return <i>{ questionAlreadyAttented ? intl.formatMessage({id:'quiz.no.already.answered'}) : intl.formatMessage({id:'quiz.no.questions'})}</i>
        }
    }

    return <Col>
        {
            [remaining_questions[0]]
                .map((question, index) => (
                    <QuizUserForm
                        key={question.id}
                        question={question}
                        index={index}
                        totalQuestions={questions.length}
                        show_quiz={show_quiz}
                        show_results={false}
                        submit_answer={submit_answer}
                        timer={timer}
                        setCurrentQuestion={setCurrentQuestion}
                    />
                ))
        }
    </Col>
}

export default QuizUser;
